import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
  useToast,
} from "@chakra-ui/react";
import * as React from "react";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { showErrorToast, showSuccessToast } from "../../utils";
import { useDispatch } from "react-redux";
import { esgAdminKeys, useUpdateOrgModules } from "../../hooks/query/esg-admin/esg-admin";
import { useQueryClient } from "react-query";
import MODULES from "config/modules";

/**
 * These are used in paths.js
 * Which works via WithAuthorization -> isModuleAvailable() -> GLOBAL.organizationModules
 * AuthProvider sets this when you login.
 */
const MODULES_LIST = Object.keys(MODULES);

const ModuleListPopup = ({ organization, isOpen, onClose }) => {
  const [selectedModules, setSelectedModules] = React.useState([]);
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (organization?.modules?.length > 0) {
      setSelectedModules(organization.modules);
    } else {
      // No modules means "all modules" — pre-select all
      setSelectedModules([...MODULES_LIST]);
    }
  }, [organization]);

  const { mutate: updateOrgModules } = useUpdateOrgModules({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, "Module list updated successfully");
      queryClient.invalidateQueries(esgAdminKeys.orgs());
      onClose();
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleSave = () => {
    // If all modules selected, save as empty (meaning "all modules")
    const modulesToSave = selectedModules.length === MODULES_LIST.length ? [] : selectedModules;
    const data = {
      modules: modulesToSave,
      tenantId: organization.tenantId,
    };
    updateOrgModules(data);
  };

  const handleToggle = (module) => {
    if (selectedModules.includes(module)) {
      setSelectedModules(selectedModules.filter((m) => m !== module));
    } else {
      setSelectedModules([...selectedModules, module]);
    }
  };

  const isAllSelected = selectedModules.length === MODULES_LIST.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <ModalHeader>Manage Modules</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Select the modules available for {organization?.name || "this organization"}.
          </Text>
          <Wrap spacing={2}>
            {MODULES_LIST.map((module) => {
              const isSelected = selectedModules.includes(module);
              return (
                <WrapItem key={module}>
                  <Tag
                    size="sm"
                    borderRadius="full"
                    bg={isSelected ? "lightLime" : "gray.100"}
                    border="1px solid"
                    borderColor={isSelected ? "lime" : "gray.300"}
                    color={isSelected ? "inherit" : "gray.400"}
                    px={3}
                    py={1}
                    cursor="pointer"
                    onClick={() => handleToggle(module)}
                  >
                    <TagLabel>{module}</TagLabel>
                    <Box as="span" ml={1} fontSize="xs">{isSelected ? "✕" : "+"}</Box>
                  </Tag>
                </WrapItem>
              );
            })}
            <WrapItem>
              <Button
                variant="link"
                size="xs"
                color="gray.500"
                fontWeight="normal"
                onClick={() => setSelectedModules(isAllSelected ? [] : [...MODULES_LIST])}
              >
                {isAllSelected ? "Deselect all" : "Select all"}
              </Button>
            </WrapItem>
          </Wrap>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModuleListPopup;
